import React from 'react';
import ImageComponent from 'next/image';
import { X } from 'lucide-react';
import { LoraModel } from '@/types/lora';
import ImageSizePreview, { aspectRatios } from '@/components/ui/image-size';
import { logoPlaceholder } from '@/utils/placeholders';

export const AdvancedSettingsModal = ({
  isOpen,
  onClose,
  currentRatio,
  setCurrentRatio,
  selectedLoraModel,
  setSelectedLoraModel,
  setIsLoraModalOpen,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentRatio: (typeof aspectRatios)[0];
  setCurrentRatio: (ratio: (typeof aspectRatios)[0]) => void;
  selectedLoraModel: LoraModel | null;
  setSelectedLoraModel: (model: LoraModel | null) => void;
  setIsLoraModalOpen: (isOpen: boolean) => void;
}) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl dark:bg-dark-200">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="mb-4 text-xl font-semibold dark:text-light-400">
          Advanced Settings
        </h2>

        <div className="space-y-6">
          <div>
            <ImageSizePreview
              currentRatio={currentRatio}
              setCurrentRatio={setCurrentRatio}
            />
          </div>

          <div>
            <h3 className="mb-3 text-lg font-medium dark:text-light-400">
              LoRA Model
            </h3>
            {selectedLoraModel ? (
              <div className="flex items-center justify-between rounded-md border border-gray-200 bg-light-100 p-3 dark:border-dark-400 dark:bg-dark-300">
                <div className="flex items-center">
                  <ImageComponent
                    src={selectedLoraModel?.image ?? logoPlaceholder}
                    alt={selectedLoraModel.name}
                    width={40}
                    height={40}
                    className="mr-3 rounded-md"
                  />
                  <div>
                    <h4 className="font-medium dark:text-light-400">
                      {selectedLoraModel.name}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-dark-700">
                      {selectedLoraModel.trigger_word}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedLoraModel(null)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsLoraModalOpen(true)}
                className="w-full rounded-md border border-dashed border-gray-300 bg-light-100 py-2 text-gray-600 duration-200 transition-colors hover:bg-light-400 dark:border-dark-400 dark:bg-dark-300 dark:text-light-400 dark:hover:bg-dark-400"
              >
                + Add LoRA Model
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
